import { useState, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import type { ApiResponse, Contact, Company } from '@nexus/shared/types';

type EntityType = 'contact' | 'company';
type EntityData = Contact | Company;

const cache = new Map<string, EntityData>();

export function useEntityPreview() {
  const [data, setData] = useState<EntityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const show = useCallback((type: EntityType, id: string) => {
    timeout.current = setTimeout(async () => {
      const key = `${type}:${id}`;
      if (cache.has(key)) {
        setData(cache.get(key)!);
        setVisible(true);
        return;
      }

      setLoading(true);
      setVisible(true);
      try {
        const endpoint = type === 'contact' ? `/api/contacts/${id}` : `/api/companies/${id}`;
        const res = await api.get<ApiResponse<EntityData>>(endpoint);
        if (res.data) {
          cache.set(key, res.data);
          setData(res.data);
        }
      } catch {
        // silently fail — hover card is non-critical
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms delay before fetching
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timeout.current);
    setVisible(false);
    setData(null);
  }, []);

  return { data, loading, visible, show, hide };
}
